import { Injectable, signal } from '@angular/core';
import { createAppKit } from '@reown/appkit';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { Config, connect, disconnect, getAccount, reconnect, switchChain, watchAccount } from '@wagmi/core';
import { defineChain } from 'viem';
import { environment } from '../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class AppKitService {

    private wagmiAdapter!: WagmiAdapter;
    private wagmiConfig!: Config;
    private appKit!: any;
    private isSwitching = false;
    private projectId = environment.PROJECT_ID;
    private initPromise: Promise<void>;
    private reconnectPromise: Promise<void> | null = null;
    private readonly RECONNECT_TIMEOUT_MS = 8000;
    private readonly ACCOUNT_SETTLE_TIMEOUT_MS = 3000;
    public walletState = signal<any>({});
    public isConnecting = signal(false);
    /** False until AppKit is created AND the saved connector has been restored */
    public isInitialized = signal(false);

    constructor() {
        this.initPromise = this.initializeAppKit();
    }

    //  FIXED: Define Amoy manually (IMPORTANT)
    private supportedNetworks: any = [
        defineChain({
            id: 80002,
            name: 'Polygon Amoy',
            network: 'polygon-amoy',
            nativeCurrency: {
                name: 'POL',
                symbol: 'POL',
                decimals: 18
            },
            rpcUrls: {
                default: {
                    // http: ['https://polygon-amoy.g.alchemy.com/v2/alch_axDvBH5lm2uxKMjo8q9r1']
                       http: ['https://polygon-amoy.drpc.org']

                }
            },
            blockExplorers: {
                default: {
                    name: 'PolygonScan',
                    url: 'https://amoy.polygonscan.com'
                }
            }
        })
    ];



    // WAIT FOR INIT
    async waitForInitialization(): Promise<void> {
        return this.initPromise;
    }

    //  GET CONFIG
    getWagmiConfig(): Config {
        return this.wagmiConfig;
    }

    //  INIT
    private async initializeAppKit() {
        try {
            this.wagmiAdapter = new WagmiAdapter({
                projectId: this.projectId,
                networks: this.supportedNetworks,
                ssr: false
            });

            this.wagmiConfig = this.wagmiAdapter.wagmiConfig as Config;

            this.appKit = createAppKit({
                adapters: [this.wagmiAdapter],
                networks: this.supportedNetworks,
                defaultNetwork: this.supportedNetworks[0],
                projectId: this.projectId,
                enableNetworkSwitch: false,
                // Pinned, so the wallet modal stays light on a dark operating system
                themeMode: 'light',
                features: {
                    analytics: false,
                    email: false,
                    socials: [],
                    onramp: false,
                    swaps: false
                },
                allWallets: 'HIDE'
            });

            // Re-attach the connector wagmi persisted in localStorage.
            // Without this, a hard refresh leaves the app disconnected (no address,
            // no signer) even though the wallet still has the site authorised.
            await this.restoreConnection();

            // Sync initial state
            const account = getAccount(this.wagmiConfig);
            this.updateState(account);

            // Watch for changes
            watchAccount(this.wagmiConfig, {
                onChange: (account) => {
                    this.updateState(account);

                    //  ONLY act when wagmi is ready
                    if (account.isConnected !== undefined) {
                        //  If already on Amoy → close modal ONCE
                        if (account.isConnected && Number(account.chainId) === 80002) {
                            setTimeout(() => {
                                this.appKit?.close();
                            }, 300); // small safe delay
                        }
                    }
                }
            });

        } catch (error) {
            console.error('AppKit init error:', error);
        } finally {
            this.isInitialized.set(true);
        }
    }

    /**
     * Best effort: silently re-attaches the previously connected wallet (no wallet
     * popup). Resolves when the attempt settles, fails or times out, so it does NOT
     * imply a connected account — use tryEnsureConnection/ensureConnection for that.
     * Concurrent callers share the same in-flight attempt.
     */
    async restoreConnection(): Promise<void> {
        if (!this.wagmiConfig) return;

        if (!this.reconnectPromise) {
            const attempt = reconnect(this.wagmiConfig)
                .then(() => {
                    this.updateState(getAccount(this.wagmiConfig));
                })
                .catch((error) => {
                    console.error('Wallet reconnect error:', error);
                });

            // An unresponsive wallet must not block the rest of the app; if the
            // attempt lands later, watchAccount still syncs the state
            const pending: Promise<void> = Promise.race([
                attempt,
                new Promise<void>(resolve => setTimeout(resolve, this.RECONNECT_TIMEOUT_MS))
            ]).finally(() => {
                // Released on the timeout too, otherwise a wallet that never answers
                // would leave this pinned and turn every later attempt into a no-op.
                // The identity check keeps a newer attempt from being cleared.
                if (this.reconnectPromise === pending) this.reconnectPromise = null;
            });

            this.reconnectPromise = pending;
        }

        return this.reconnectPromise;
    }

    /** An account is only usable for reads/signing once wagmi reports both */
    private isUsableAccount(account: any): boolean {
        return !!(account?.isConnected && account?.address);
    }

    /**
     * Returns the connected account, restoring a dropped session first (e.g. right
     * after a page refresh). Returns null when the wallet is genuinely not
     * connected, so callers can fall back instead of failing blindly.
     */
    async tryEnsureConnection(): Promise<any | null> {
        await this.waitForInitialization();

        let account = getAccount(this.wagmiConfig);
        if (this.isUsableAccount(account)) return account;

        await this.restoreConnection();
        account = getAccount(this.wagmiConfig);

        // restoreConnection can return early on its timeout while the wallet is
        // still answering, so give a pending handshake a bounded chance to land
        if (!this.isUsableAccount(account)) {
            account = await this.waitForAccountToSettle();
        }

        this.updateState(account);
        return this.isUsableAccount(account) ? account : null;
    }

    /**
     * Same as tryEnsureConnection but throws when the wallet is still not
     * connected, so sign/transaction flows fail fast with an actionable message
     * instead of erroring deeper in the wallet SDK.
     */
    async ensureConnection(): Promise<any> {
        const account = await this.tryEnsureConnection();
        if (!account) {
            throw new Error('Wallet not connected. Please reconnect your wallet and try again.');
        }

        return account;
    }

    /**
     * Waits while wagmi is mid-handshake, bounded so a wallet that never answers
     * cannot hang the caller. Returns as soon as the status settles.
     */
    private async waitForAccountToSettle(): Promise<any> {
        const deadline = Date.now() + this.ACCOUNT_SETTLE_TIMEOUT_MS;
        let account = getAccount(this.wagmiConfig);

        while (
            (account?.status === 'connecting' || account?.status === 'reconnecting') &&
            Date.now() < deadline
        ) {
            await new Promise(resolve => setTimeout(resolve, 150));
            account = getAccount(this.wagmiConfig);
        }

        return account;
    }

    /**
     * The EIP-1193 provider of the connector actually in use, which is what
     * ethers must wrap — window.ethereum is unreliable when several wallet
     * extensions are installed. Null when no wallet is connected, letting the
     * caller fall back to the injected provider.
     */
    async getConnectedProvider(): Promise<any | null> {
        const account = await this.tryEnsureConnection();
        if (!account?.connector) return null;

        try {
            return await account.connector.getProvider();
        } catch (error) {
            console.error('Unable to read wallet provider:', error);
            return null;
        }
    }

    //  UPDATE STATE
    private updateState(account: any) {
        this.walletState.set({
            isConnected: account.isConnected,
            address: account.address,
            chainId: account.chainId,
            network: account.chain?.name,
            isSupportedNetwork: Number(account.chainId) === 80002
        });
    }

    //  DISCONNECT
    async disconnect() {
        await this.waitForInitialization();
        try {
            await disconnect(this.wagmiConfig);
        } catch (error) {
            console.error('Error disconnecting:', error);
        }
    }

    // GET CURRENT STATE
    getCurrentWalletState() {
        return this.walletState();
    }

    // WAIT FOR WALLET
    private async waitForWalletConnection(): Promise<void> {
        return new Promise((resolve) => {
            const interval = setInterval(() => {
                const account = getAccount(this.wagmiConfig);
                if (account?.address) {
                    clearInterval(interval);
                    resolve();
                }
            }, 300);
        });
    }

    // 🔹 CHECK CHAIN
    isOnChain(requiredChainId: number): boolean {
        const account = getAccount(this.wagmiConfig);
        return Number(account.chainId) === Number(requiredChainId);
    }

    // 🔹 CONNECT WALLET
    async connectWallet(connectorId?: string): Promise<any> {
        await this.waitForInitialization();
        try {
            // 🔸 MetaMask direct connect
            if (connectorId === 'metaMask') {
                const currentAccount = getAccount(this.wagmiConfig);
                if (currentAccount.isConnected && currentAccount.address) {
                    return currentAccount;
                }

                const connectors = this.wagmiConfig.connectors;

                const connector = connectors.find(
                    c =>
                        c.id === 'io.metamask' ||
                        c.name === 'MetaMask' ||
                        (c as any)._isMetaMask
                );

                if (connector) {
                    this.setWalletConnecting(true);

                    await connect(this.wagmiConfig, { connector });

                    await this.waitForWalletConnection();

                    const account = getAccount(this.wagmiConfig);
                    if (Number(account.chainId) !== 80002) {
                        try {
                            await switchChain(this.wagmiConfig, {
                                chainId: 80002
                            });
                        } catch (e) {
                            console.log('User rejected switch');
                        }
                    }
                    this.setWalletConnecting(false);
                    return account;
                }
            }

            //  AppKit modal connect - check state first
            const state = getAccount(this.wagmiConfig);
            if (state.isConnected) {
                await this.appKit.open({ view: 'Account' });
            } else {
                await this.appKit.open({ view: 'Connect' });
            }
            this.setWalletConnecting(true);

            await this.waitForWalletConnection();

            const account = getAccount(this.wagmiConfig);
            this.validateConnectedAccount(account.address);
            this.setWalletConnecting(false);
            return account;

        } catch (error) {
            console.error('Error connecting wallet:', error);
            this.setWalletConnecting(false);
            
            const account = getAccount(this.wagmiConfig);
            const errorName = (error as any)?.name || '';
            const errorMessage = ((error as any)?.message || '').toLowerCase();

            // If it's just an "already connected" error, treat as success
            if (
                account.isConnected &&
                account.address &&
                (errorName === 'ConnectorAlreadyConnectedError' || errorMessage.includes('already connected'))
            ) {
                this.validateConnectedAccount(account.address);
                return account;
            }

            throw error; // Rethrow to allow UI to handle specific error codes (like -32002)
        }
    }

    private validateConnectedAccount(newAddress?: string): void {
        if (!newAddress) return;

        try {
            const userDataStr = localStorage.getItem('usr_meta'); // STORAGE_KEYS.USER_META_INFO
            if (userDataStr) {
                const userData = JSON.parse(userDataStr);
                const loggedInAddress = userData?.public_address || userData?.publicAddress;
                
                if (loggedInAddress && newAddress.toLowerCase() !== loggedInAddress.toLowerCase()) {
                    throw new Error("Account mismatch: The connected wallet does not match the logged-in user.");
                }
            }
        } catch (e) {
            if (e instanceof Error && e.message.includes("Account mismatch")) {
                throw e;
            }
            console.error("Validation error in AppKit:", e);
        }
    }

    //  HELPER TO MANAGE LOADING STATE
    private setWalletConnecting(state: boolean) {
        this.isConnecting.set(state);
    }
}
